import React, { useEffect } from "react";
import Button from "../../components/Button";
import InputBox from "../../components/InputBox";
import { useParams } from "react-router-dom";
import { useState } from "react";
import { useSelector } from "react-redux";
import {
  addNewDoc,
  getAllBuildingSpaces,
  setDocData,
  deleteDocById,
} from "../../Firebase/Firebase.utils";
import { toast } from "react-toastify";
import { FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import InventoryForm from "./InventoryForm";

export default function Inventory() {
  const [formData, setFormData] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [totalSpace, setTotalSpace] = useState(0);
  const [editMode, setEditMode] = useState(false);

  const id = useParams();
  const currentUser = useSelector((state) => state.userObject);

  // get all inventory
  useEffect(() => {
    getAllBuildingSpaces("inventory", id.id, (data) => {
      // arrange data in ascending order of floor number
      data.sort((a, b) => a.floorNumber - b.floorNumber);
      setInventory(data);
    });
  }, []);

  // get total space of all units
  useEffect(() => {
    const totalSpaceOfUnits = inventory.reduce((acc, item) => {
      // convert area to number
      const area = parseFloat(item.area);
      return acc + area;
    }, 0);
    setTotalSpace(totalSpaceOfUnits);
    setDocData("masterlist", id.id, { totalAvailableSpace: totalSpaceOfUnits });
  }, [inventory]);

  // create object to be added to firebase
  const createObject = (data) => {
    return {
      ...data,
      buildingId: id.id,
      createdBy: currentUser.email,
      createdOn: new Date(),
      updatedBy: currentUser.email,
      updatedOn: new Date(),
    };
  };

  const handleAddInventory = (e) => {
    e.preventDefault();
    if (editMode) {
      handleUpdateInventory();
      setEditMode(false);
    } else {
      handleCreateInventory();
    }
    setFormData({});
  };

  const handleCreateInventory = () => {
    setIsProcessing(true);
    setFormData({});
    const data = createObject(formData);
    addNewDoc("inventory", data)
      .then((res) => {
        toast.success("Inventory added successfully");
        setShowForm(false);
        setIsProcessing(false);
      })
      .catch((err) => {
        toast.error(err.message);
        setIsProcessing(false);
      });
  };

  const handleUpdateInventory = () => {
    setIsProcessing(true);
    const data = createObject(formData);
    setDocData("inventory", formData.id, data)
      .then((res) => {
        toast.success("Inventory updated successfully");
        setShowForm(false);
        setIsProcessing(false);
      })
      .catch((err) => {
        toast.error(err.message);
        setIsProcessing(false);
      });
    setFormData({});
  };

  const handleShowForm = () => {
    if (!showForm) {
      // Reset form data when opening the form
      setFormData({});
      setEditMode(false);
    }
    setShowForm(!showForm);
  };

  const handleEdit = (e, item) => {
    setEditMode(true);
    setFormData(item);
    setShowForm(true);
  };

  const handleDelete = (e, item) => {
    if (window.confirm("Are you sure you want to delete this inventory?")) {
      deleteDocById("inventory", item.id)
        .then((res) => {
          toast.success("Inventory deleted successfully");
        })
        .catch((err) => {
          toast.error(err.message);
        });
    }
  };

  // headers and data for form and table
  const tableHeadersAndData = [
    {
      header: "Floor",
      data: "floorNumber",
      required: true,
      placeholder: "e.g. 6",
    },
    {
      header: "Unit",
      data: "unitNumber",
      required: true,
      placeholder: "e.g. 601",
    },
    {
      header: "Area in sqm",
      data: "area",
      required: true,
      placeholder: "e.g. 1000",
      type: "number",
      formatStringToFloat: true,
    },
    {
      header: "Parking Allocation",
      data: "parkingAllocation",
      required: true,
      placeholder: "e.g. 1:100",
    },
    {
      header: "Quoted Rent",
      data: "quotedRent",
      required: true,
      type: "number",
      placeholder: "e.g. 1000",
    },
    {
      header: "Building Dues",
      data: "buildingDues",
      required: true,
      placeholder: "e.g. 160",
      type: "text",
    },
    {
      header: "Handover Condition",
      data: "handoverCondition",
      required: true,
      placeholder: "e.g. As is",
    },
    {
      header: "AC Charge",
      data: "acCharge",
      required: true,
      placeholder: "e.g. 150",
      type: "text",
    },
    {
      header: "Parking Rate /month",
      data: "parkingRate",
      required: false,
      placeholder: "e.g. 5000",
      type: "number",
    },
    {
      header: "Availability",
      data: "availabilityDate",
      required: true,
      placeholder: "e.g. 2024",
    },
    {
      header: "Remarks",
      data: "remarks",
      required: false,
      placeholder: "e.g. Good for BPO company",
    },
  ];

  // fields that need to be converted to float
  const fieldsForConversion = ["area", "parkingRate", "quotedRent"];

  return (
    <div className="flex flex-col bg-white min-h-screen">
      <div className="bg-[#002368] text-white py-4 px-6 mb-6 rounded-md shadow-md">
        <h1 className="text-2xl font-bold">Inventory Information</h1>
      </div>

      <div className="px-6">
        <Button
          className="flex items-center gap-2 bg-[#ff5003] text-white py-2 px-4 rounded-md shadow hover:shadow-lg transition-all duration-300"
          label={
            <div className="flex items-center gap-2">
              <FaPlus />
              <span>Add Available Space</span>
            </div>
          }
          onClick={handleShowForm}
        />
      </div>

      {/* Form Modal */}
      <InventoryForm
        showForm={showForm}
        handleShowForm={handleShowForm}
        formData={formData}
        setFormData={setFormData}
        handleAddInventory={handleAddInventory}
        isProcessing={isProcessing}
        editMode={editMode}
        tableHeadersAndData={tableHeadersAndData}
      />

      <div className="mt-8 bg-white px-6 py-4 rounded-lg shadow-sm mx-6 mb-6">
        <div className="uppercase font-bold text-[#002368] mb-4 text-sm border-b border-gray-200 pb-2">
          Available Spaces
        </div>

        {/* table component */}
        <div className="overflow-x-auto">
          <table className="w-full bg-white border border-gray-200 text-sm">
            <thead>
              <tr>
                {
                  // table headers
                  tableHeadersAndData.map((item) => (
                    <th
                      key={item.header}
                      className="border border-gray-300 px-3 py-2 bg-[#002368] text-white font-medium"
                    >
                      {item.header}
                    </th>
                  ))
                }
                <th className="border border-gray-300 px-3 py-2 bg-[#002368] text-white font-medium">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="text-center">
              {inventory.length > 0 ? (
                inventory.map((item, index) => (
                  <tr
                    key={index}
                    className={`${
                      index % 2 === 0 ? "bg-gray-50" : "bg-white"
                    } hover:bg-gray-100 transition-colors duration-150`}
                  >
                    {
                      // table data
                      tableHeadersAndData.map((header) => (
                        <td
                          key={header.header}
                          className="border border-gray-200 px-3 py-2 text-gray-700"
                        >
                          {item[header.data] === undefined
                            ? item[header.data]
                            : fieldsForConversion.includes(header.data)
                            ? parseFloat(
                                item[header.data].replace(/,/g, "")
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })
                            : item[header.data]}
                        </td>
                      ))
                    }
                    <td className="border border-gray-200 px-3 py-2">
                      <div className="flex justify-center gap-3">
                        <button
                          onClick={(e) => handleEdit(e, item)}
                          className="text-[#002368] hover:text-[#001a4d] transition-colors"
                        >
                          <FaEdit size={16} />
                        </button>
                        <button
                          onClick={(e) => handleDelete(e, item)}
                          className="text-[#ff5003] hover:text-red-700 transition-colors"
                        >
                          <FaTrash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={tableHeadersAndData.length + 1}
                    className="py-4 text-center text-gray-500"
                  >
                    No inventory items available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="font-semibold mt-6 text-[#002368] text-sm bg-gray-50 p-3 rounded-md inline-block">
            Total Available Space:{" "}
            <span className="text-[#ff5003]">
              {totalSpace.toLocaleString()}
            </span>{" "}
            square meters
          </div>
        </div>
      </div>
    </div>
  );
}
